import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarDay,
  faComments,
  faCheckCircle,
  faClock,
  faUserInjured,
  faPhoneAlt,
} from "@fortawesome/free-solid-svg-icons";
import styles from "./DoctorPortal.module.css";

const DoctorSchedule = ({
  appointments = [],
  handleStatusChange,
  saveEditedStatus,
  editedStatus = {},
  onOpenChat,
}) => {
  const [selectedDate, setSelectedDate] = useState(() => {
    return new Date().toISOString().split("T")[0];
  });

  // Helper formatting
  function formatTimeToAMPM(time) {
    if (!time) return "10:00 AM";
    const [hours, minutes] = time.split(":");
    let period = "AM";
    let formattedHours = parseInt(hours, 10);
    if (formattedHours >= 12) {
      period = "PM";
      if (formattedHours > 12) formattedHours -= 12;
    }
    return `${formattedHours}:${minutes} ${period}`;
  }

  // Filter appointments for the selected date
  const filteredApps = appointments.filter(
    (ap) => ap.appointmentDate === selectedDate
  );

  return (
    <div>
      {/* View Header */}
      <div className={styles.viewHeader}>
        <div>
          <h1 className={styles.viewTitle}>Clinical Schedule</h1>
          <p className={styles.viewSubtitle}>
            Manage daily appointments and patient consultations
          </p>
        </div>
        <div className={styles.metricBadge}>
          <FontAwesomeIcon icon={faCalendarDay} />
          <span>
            {filteredApps.length} {filteredApps.length === 1 ? "Appointment" : "Appointments"}
          </span>
        </div>
      </div>

      {/* Date Picker Bar */}
      <div className={styles.glassCard} style={{ marginBottom: "2rem", display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
        <label htmlFor="scheduleDate" style={{ fontWeight: 750, color: "#0f172a", fontSize: "0.95rem" }}>
          Select Date:
        </label>
        <input
          id="scheduleDate"
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          className={styles.inputField}
          style={{ width: "auto", minWidth: "180px", fontWeight: 700 }}
        />
        <button
          type="button"
          onClick={() => setSelectedDate(new Date().toISOString().split("T")[0])}
          className={styles.ghostBtn}
        >
          Today
        </button>
      </div>

      {/* Appointments List */}
      <div className={styles.glassCard} style={{ padding: 0, overflow: "hidden" }}>
        {filteredApps.length === 0 ? (
          <div style={{ padding: "4rem 2rem", textAlign: "center", color: "#64748b" }}>
            <FontAwesomeIcon icon={faClock} style={{ fontSize: "2.5rem", color: "#cbd5e1", marginBottom: "1rem" }} />
            <h3 style={{ fontSize: "1.25rem", fontWeight: 800, color: "#0f172a", margin: 0 }}>
              No appointments on {selectedDate}
            </h3>
            <p style={{ margin: "6px 0 0 0", fontSize: "0.92rem" }}>
              There are currently no patient visits booked for this date.
            </p>
          </div>
        ) : (
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Time Slot</th>
                  <th>Patient Name</th>
                  <th>Mobile Number</th>
                  <th>Concern</th>
                  <th>Status</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredApps.map((ap) => {
                  const patientName = ap.patient
                    ? `${ap.patient.firstName || ""} ${ap.patient.lastName || ""}`.trim() || ap.patient.name || "Verified Patient"
                    : "Verified Patient";
                  const phone = ap.patient?.contactNumber || ap.patient?.phone || "N/A";
                  const timeStr = ap.slot?.startTime ? formatTimeToAMPM(ap.slot.startTime) : "10:00 AM";
                  const currentStatus = editedStatus[ap._id] !== undefined ? editedStatus[ap._id] : (ap.status || "scheduled");

                  return (
                    <tr key={ap._id}>
                      <td style={{ fontWeight: 800, color: "#2d6a4f", whiteSpace: "nowrap" }}>
                        {timeStr}
                      </td>
                      <td style={{ fontWeight: 750, color: "#0f172a" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <FontAwesomeIcon icon={faUserInjured} style={{ color: "#94a3b8" }} />
                          <span>{patientName}</span>
                        </div>
                      </td>
                      <td style={{ color: "#64748b" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                          <FontAwesomeIcon icon={faPhoneAlt} style={{ fontSize: "0.75rem", color: "#cbd5e1" }} />
                          <span>{phone}</span>
                        </div>
                      </td>
                      <td style={{ color: "#475569", fontWeight: 500 }}>
                        {ap.disease || "General Checkup"}
                      </td>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <select
                            value={currentStatus}
                            onChange={(e) => handleStatusChange(e, ap)}
                            className={styles.selectField}
                            style={{
                              borderColor: currentStatus === "completed" ? "#86efac" : currentStatus === "canceled" ? "#fca5a5" : "#cbd5e1",
                              background: currentStatus === "completed" ? "#f0fdf4" : currentStatus === "canceled" ? "#fef2f2" : "#ffffff",
                              color: currentStatus === "completed" ? "#166534" : currentStatus === "canceled" ? "#991b1b" : "#0f172a",
                            }}
                          >
                            <option value="scheduled">Scheduled</option>
                            <option value="completed">Completed</option>
                            <option value="canceled">Canceled</option>
                          </select>
                          {editedStatus[ap._id] && editedStatus[ap._id] !== ap.status && (
                            <button
                              type="button"
                              onClick={() => saveEditedStatus(ap._id)}
                              className={styles.primaryBtn}
                              style={{ padding: "0.4rem 0.8rem", fontSize: "0.8rem" }}
                            >
                              Save
                            </button>
                          )}
                        </div>
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {ap.patient && (
                          <button
                            type="button"
                            onClick={() => onOpenChat(ap.patient)}
                            className={styles.ghostBtn}
                            style={{ padding: "0.45rem 1rem" }}
                          >
                            <FontAwesomeIcon icon={faComments} />
                            <span>Chat</span>
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DoctorSchedule;
